import { Injectable } from '@angular/core';
import { TileType } from '@groundworks/ngx-groundworks-ui';
import { AttachmentInfo } from '../sdk/sdk';

export interface FileBadge {
  iconClass: string;
  textClass: string;
}

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'svg',
  'webp',
  'heic',
]);

const DOCUMENT_EXTENSION_MAP: Record<string, TileType> = {
  pdf: 'pdf',
  doc: 'document',
  docx: 'document',
  txt: 'document',
  rtf: 'document',
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  zip: 'compressed',
  rar: 'compressed',
  '7z': 'compressed',
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  wmv: 'video',
};

const VIDEO_EXTENSION_MAP: Record<string, TileType> = {
  mp4: 'video',
  mov: 'video',
  avi: 'video',
  wmv: 'video',
  flv: 'video',
  mkv: 'video',
  webm: 'video',
  m4v: 'video',
};

const SPREADSHEET_EXTENSION_MAP: Record<string, TileType> = {
  xls: 'spreadsheet',
  xlsx: 'spreadsheet',
  csv: 'spreadsheet',
  ods: 'spreadsheet',
};

const COMPRESSED_EXTENSION_MAP: Record<string, TileType> = {
  zip: 'compressed',
  rar: 'compressed',
  '7z': 'compressed',
  tar: 'compressed',
  gz: 'compressed',
};

@Injectable({
  providedIn: 'root',
})
export class FileUtilityService {
  getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot >= 0 ? fileName.substring(lastDot + 1).toLowerCase() : '';
  }

  isImageFile(attachment: AttachmentInfo): boolean {
    const ext = this.getFileExtension(attachment.fileName ?? '');
    return IMAGE_EXTENSIONS.has(ext);
  }

  isImageFileType(file: File): boolean {
    return file.type?.startsWith('image/') ?? false;
  }

  getFileType(attachment: AttachmentInfo): TileType {
    const ext = this.getFileExtension(attachment.fileName ?? '');
    if (IMAGE_EXTENSIONS.has(ext)) {
      return 'image';
    }
    return DOCUMENT_EXTENSION_MAP[ext] ?? 'document';
  }

  /**
   * Gets a file badge (icon and text class) for a File object
   * @param file - The File object to get badge for
   * @returns FileBadge with icon and text classes
   */
  getFileBadge(file: File): FileBadge {
    const ext = this.getFileExtensionFromFile(file).toLowerCase();
    const isImage = this.isImageFileType(file);
    const isVideo = file.type?.startsWith('video/') ?? false;

    if (isImage) {
      return {
        iconClass: 'bi-image',
        textClass: 'text-primary',
      };
    }

    if (ext === 'pdf') {
      return {
        iconClass: 'bi-filetype-pdf',
        textClass: 'text-danger',
      };
    }

    if (DOCUMENT_EXTENSION_MAP[ext]) {
      return {
        iconClass: 'bi-file-earmark-word',
        textClass: 'text-secondary',
      };
    }

    if (SPREADSHEET_EXTENSION_MAP[ext]) {
      return {
        iconClass: 'bi-file-earmark-spreadsheet',
        textClass: 'text-success',
      };
    }

    if (COMPRESSED_EXTENSION_MAP[ext]) {
      return {
        iconClass: 'bi-file-zip',
        textClass: 'text-warning',
      };
    }

    if (isVideo || VIDEO_EXTENSION_MAP[ext]) {
      return {
        iconClass: 'bi-file-earmark-play',
        textClass: 'text-primary',
      };
    }

    return {
      iconClass: 'bi-file',
      textClass: 'text-muted',
    };
  }

  /**
   * Extracts file extension from a File object
   * @param file - The File object
   * @returns The file extension in uppercase (max 3 chars)
   */
  private getFileExtensionFromFile(file: File): string {
    const parts = file.name?.split('.') ?? [];
    const ext = parts.length > 1 ? parts.pop() : null;
    if (ext) {
      return ext.substring(0, 3).toUpperCase();
    }
    if (file.type) {
      return file.type.split('/').pop()?.substring(0, 3).toUpperCase() ?? 'FILE';
    }
    return 'FILE';
  }
}
